import AddWalletIcon from "@/assets/add-wallet.svg?react";
import Session from "supertokens-auth-react/recipe/session";
import InitPaymentUser from "@/components/InitPayment/InitPaymentUser";
import InitPaymentCorporateUser from "@/components/InitPayment/InitPaymentCorporateUser";
import PaymentHeader from "@/components/InitPayment/InitPaymentHeader";
import BankAccount from "@/components/BankAccount";
import AddAccount from "@/components/AddAccount";
import CorporateUsers from "@/components/CorporateUsers";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Field,
  FieldContent,
  FieldLabel,
  FieldTitle,
} from "@/components/ui/field";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Spinner } from "@/components/ui/spinner";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CircleX, LogOut } from "lucide-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { addMinutes, fromUnixTime, getUnixTime } from "date-fns";
import { GetAuthUserStatus, initAudkenni } from "@/api/auth";
import {
  addBankAccount,
  getAccountRequest,
  getAccounts,
  getCorporateAccounts,
  requestBankAccountConsent,
  validateBBAN,
} from "@/api/account";
import { cancelPayment, initWalletPayment, patchPayment } from "@/api/payment";
import { getCorporateUsers, getLoggedInUser } from "@/api/users";
import { queryClient } from "@/entry-client";
import {
  FULL_WIDTH_TALL_BUTTON,
  GREEN_ACTION_BUTTON_DISABLED,
  GREEN_ACTION_BUTTON_ENABLED,
  OUTLINE_PRESSABLE_BUTTON,
} from "@/lib/buttonStyles";
import { getBBAN } from "@/lib/utils";
import type { Payment } from "@/model/payment";
import {
  type AccountConsentRequest,
  type BankAccount as BankAccountType,
} from "@/model/account";

interface Props {
  payment: Payment | null | undefined;
  setRefetchInterval: (value: number) => void;
}

// TODO: Clean up this state
export default function InitPayment({ payment, setRefetchInterval }: Props) {
  // Auth
  const [openWallet, setOpenWallet] = useState<boolean>(false);
  const [openAddAccount, setOpenAddAccount] = useState<boolean>(false);
  const [selectedTab, setSelectedTab] = useState<string>("user");
  const [authTab, setAuthTab] = useState<string>("audkenniSim");
  const [authPhoneNumber, setAuthPhoneNumber] = useState<string>();
  const [authExternalId, setAuthExternalId] = useState<string>("");
  const [authenticating, setAuthenticating] = useState<boolean>(false);
  const [loggedIn, setLoggedIn] = useState<boolean>(false);
  const [isSessionCheckLoading, setIsSessionCheckLoading] =
    useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // Add account state
  const [accountBank, setAccountBank] = useState<string>("");
  const [accountReikn, setAccountReikn] = useState<string>("");
  const [accountHb, setAccountHb] = useState<string>("");
  const [corporateId, setCorporateId] = useState<string>("");
  const [selectedCorporateUserId, setSelectedCorporateUserId] =
    useState<string>("");
  // Data fetching
  const [fetchAccounts, setFetchAccounts] = useState<boolean>(false);
  const [refetchAuthInterval, setRefetchAuthInterval] = useState(0);
  const [refetchAccountRequestInterval, setRefetchAccountRequestInterval] =
    useState<number>(0);
  const authenticateMutation = useMutation({
    mutationFn: async () => {
      return initAudkenni(authTab, authExternalId, `+354${authPhoneNumber}`);
    },
    onSuccess: async () => {
      setRefetchAuthInterval(750);
    },
    onError: (error: Response) => {
      // Redirect to the light onboarding if the user it not onboareded. That is the case when the response is 404
      if (error.status === 404) {
        patchAndRedirect();
      } else {
        toast.error("Villa kom við auðkenningu");
      }
    },
  });

  const initWalletPaymentMutation = useMutation({
    mutationFn: async (variables: { account: BankAccountType }) => {
      return initWalletPayment(String(payment?.id), authTab, variables.account);
    },
    onSuccess: async (data, variables) => {
      if (data.consentExpired) {
        toast.error(
          "Auðkenning hjá banka er útrunnin, við sendum þig í auðkenningu...",
        );
        setTimeout(async () => {
          await requestBankConsent(variables.account);
        }, 3000); // give the user some time to read the toast message
      } else {
        setRefetchInterval(750);
      }
    },
    onError: () => {
      setIsLoading(false);
      toast.error("Ekki tókst að framkvæma greiðslu");
    },
  });

  const requestAccountConsent = useMutation({
    mutationFn: async (variables: { request: AccountConsentRequest }) => {
      return requestBankAccountConsent(variables.request);
    },
    onError: () => {
      toast.warning("Villa kom upp við að bæta við reikning.");
    },
  });

  const addNewBankAccount = useMutation({
    mutationFn: async () => {
      const bban = getBBAN({
        bank: accountBank,
        hb: accountHb,
        reikn: accountReikn,
      });
      return addBankAccount(bban, corporateId);
    },
    onSuccess: (data) => {
      // If the added bank account is Islandsbanki. We request the consent
      if (data.bankId === "isb") {
        requestBankConsent(data);
        toast.info("Auðkenning hjá banka nauðsynleg, við áframsendum þig...");
      } else {
        toast.success("Reikning bætt við");
      }
      queryClient.invalidateQueries({ queryKey: ["accounts"] });
      queryClient.invalidateQueries({
        queryKey: [
          "corporate-accounts",
          loggedInUser?.externalId,
          selectedCorporateUserId,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: ["corporate-users", loggedInUser?.externalId],
      });
      setAccountBank("");
      setAccountReikn("");
      setCorporateId("");
      setOpenAddAccount(false);
    },
    onError: () => {
      toast.error("Ekki tókst að bæta við reikning");
    },
  });

  const validateAccount = useMutation({
    mutationFn: async () => {
      const bban = getBBAN({
        bank: accountBank,
        hb: accountHb,
        reikn: accountReikn,
      });
      const externalIdToUse =
        selectedTab === "user" ? loggedInUser?.externalId : corporateId;
      return validateBBAN(bban, externalIdToUse);
    },
    onSuccess: (data) => {
      if (data.isValid) {
        addNewBankAccount.mutate();
      }
      if (!data.isValid) {
        toast.error(data.errorMessage);
      }
    },
    onError: () => {
      toast.error("Ekki tókst að bæta við reikning");
    },
  });

  const requestBankConsent = async (account: BankAccountType) => {
    await requestAccountConsent.mutateAsync({
      request: {
        userId: String(loggedInUser?.id),
        bankId: account.bankId,
        accountId: account.id,
        paymentId: String(payment?.id),
        externalId: account.externalId,
      },
    });
    setRefetchAuthInterval(0);
    setRefetchAccountRequestInterval(750);
  };

  const patchPaymentMutation = useMutation({
    mutationFn: async (variables: {
      phoneNumber?: string;
      externalId?: string;
      debtorCorpExternalId?: string;
      expiresAt: number;
    }) => {
      return patchPayment(
        String(payment?.id),
        variables.phoneNumber,
        variables.externalId,
        variables.debtorCorpExternalId,
        variables.expiresAt,
      );
    },
  });

  const cancelPaymentMutation = useMutation({
    mutationFn: async () => {
      return cancelPayment(String(payment?.id));
    },
    onSuccess: () => {
      setRefetchInterval(750);
    },
    onError: () => {
      toast.error("Ekki tókst að hætta við greiðslu");
    },
  });

  const { data: authData } = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: ["GetAuthUserStatus"],
    queryFn: () => {
      return GetAuthUserStatus(String(authenticateMutation?.data?.id));
    },
    enabled: !!authenticateMutation?.data?.id,
    refetchInterval: refetchAuthInterval,
    retry: (_, error) => {
      // We want the network request to stop if the response is an error. We don't need the counter for now.
      if (error) {
        return false;
      }
      return true;
    },
  });

  const { data: loggedInUser } = useQuery({
    queryKey: ["loggedInUser"],
    queryFn: () => getLoggedInUser(),
    enabled: fetchAccounts, //TODO: Rename this state variable into something more genetic auth type.
    retry: (_, error) => {
      // We want the network request to stop if the response is an error. We don't need the counter for now.
      if (error) {
        return false;
      }
      return true;
    },
  });

  const { data: accounts } = useQuery({
    queryKey: ["accounts"],
    queryFn: () => getAccounts(),
    enabled: fetchAccounts, //TODO: Rename this state variable into something more genetic auth type.
    retry: (_, error) => {
      // We want the network request to stop if the response is an error. We don't need the counter for now.
      if (error) {
        return false;
      }
      return true;
    },
  });

  const { data: corporateAccounts } = useQuery({
    queryKey: [
      "corporate-accounts",
      loggedInUser?.externalId,
      selectedCorporateUserId,
    ],
    queryFn: () =>
      getCorporateAccounts(
        selectedCorporateUserId,
      ),
    enabled: !!loggedInUser?.externalId && selectedCorporateUserId.length > 0, //TODO: Rename this state variable into something more genetic auth type.
    retry: (_, error) => {
      // We want the network request to stop if the response is an error. We don't need the counter for now.
      if (error) {
        return false;
      }
      return true;
    },
  });

  //TODO: Move the corporate users logic into a component. This is just a POC.
  const { data: corporateUsers } = useQuery({
    queryKey: ["corporate-users", loggedInUser?.externalId],
    queryFn: () => getCorporateUsers(String(loggedInUser?.externalId)),
    enabled: !!loggedInUser?.externalId,
    retry: (_, error) => {
      // We want the network request to stop if the response is an error. We don't need the counter for now.
      if (error) {
        return false;
      }
      return true;
    },
  });

  /** Polling of account request for Íslandsbanki accounts, this needs to be done because the don't support consentless payments */
  const { data: pollAccountReq } = useQuery({
    queryKey: ["pollAccountReq", { id: requestAccountConsent.data?.id }],
    queryFn: () =>
      requestAccountConsent.data?.id !== ""
        ? getAccountRequest(String(requestAccountConsent?.data?.id))
        : null,
    refetchInterval: refetchAccountRequestInterval,
    enabled: requestAccountConsent.isSuccess,
    retry: (_, error) => {
      // We want the network request to stop if the response is an error. We don't need the counter for now.
      if (error) {
        return false;
      }
      return true;
    },
  });

  const authenticate = () => {
    authenticateMutation.mutate();
    setAuthenticating(true);
  };

  const cancelAuth = () => {
    authenticateMutation.reset();
    queryClient.invalidateQueries({
      queryKey: ["GetAuthUserStatus"],
    });
    setRefetchAuthInterval(0);
    setOpenWallet(false);
    setAuthenticating(false);
    setIsLoading(false);
    setAuthPhoneNumber("");
    setAuthExternalId("");
  };

  const extendPaymentLifetime = async () => {
    // Add 5 minutes to expiresAt. If for some reason the payment.expiresAt is not set we simply use the current time and add 5 minutes to it.
    const expirationDate = fromUnixTime(
      payment?.expiresAt
        ? Number(payment?.expiresAt)
        : Math.floor(Date.now() / 1000),
    );
    const extendedExpiryDate = addMinutes(expirationDate, 5);
    // Extend the payment lifetime to 5 minutes on
    await patchPaymentMutation.mutateAsync({
      phoneNumber: authPhoneNumber,
      externalId: authExternalId,
      debtorCorpExternalId: "",
      expiresAt: getUnixTime(extendedExpiryDate),
    });
  };

  const logout = async () => {
    await Session.signOut();
    queryClient.invalidateQueries();
    setLoggedIn(false);
  };

  const patchAndRedirect = async () => {
    try {
      await extendPaymentLifetime();
      // Redirect to the onboarding page
      window.location.href = window.location.href.replace("payment", "light");
    } catch (error) {
      console.error("Error extending payment lifetime:", error);
      toast.error("Ekki tókst að uppfæra greiðslu.");
    }
  };

  const onInitWalletPayment = (account: BankAccountType) => {
    initWalletPaymentMutation.mutate({
      account,
    });
  };

  const addAccount = () => {
    validateAccount.mutate();
  };

  useEffect(() => {
    if (loggedInUser) {
      // If the user has onboardedAt set to 0 we need to redirect him to the onboarding page.
      // This indicates that the user was created through direct payments as a shadow user.
      // PatchAndRedirect will extend the payment lifetime and redirect the user to the onboarding page.
      if (loggedInUser.onboardedAt === 0) {
        // Added inline async function so to await the function call. Yeahh...bit nasty but useEffects are like this.
        (async () => {
          await patchAndRedirect();
        })(); // Invoke it right away.
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedInUser]);

  // React to auth status only; callbacks omitted to avoid re-running every render.
  useEffect(() => {
    if (authData) {
      switch (authData.status) {
        case "SUCCESS":
          setRefetchAuthInterval(0);
          setFetchAccounts(true);
          setLoggedIn(true);
          setAuthenticating(false);
          setOpenWallet(false);
          setAuthPhoneNumber("");
          setAuthExternalId("");
          extendPaymentLifetime();
          authenticateMutation.reset();
          break;
        case "FAILED":
          cancelAuth();
          break;
        case "EXPIRED":
          cancelAuth();
          break;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only run when authData changes; callbacks are stable in practice.
  }, [authData]);

  // Only want to run this once on mount.
  useEffect(() => {
    if (corporateUsers && corporateUsers.length > 0) {
      setSelectedCorporateUserId(corporateUsers[0].id);
    } else {
      return setSelectedCorporateUserId("");
    }
  }, [corporateUsers]);

  // account request polling for Íslandsbanki accounts
  useEffect(() => {
    if (pollAccountReq) {
      if (pollAccountReq.status === "SCA_REQUIRED") {
        setRefetchInterval(0);
        window.location.assign(pollAccountReq.scaRedirectUrl);
      }
      if (
        ["ERROR", "REJECTED", "SUCCESS", "CANCELLED"].includes(
          pollAccountReq.status,
        )
      ) {
        // Stop the polling
        setRefetchInterval(0);
      }
    }
  }, [pollAccountReq, setRefetchInterval]);

  useEffect(() => {
    let isMounted = true;

    Session.doesSessionExist()
      .then((exists) => {
        if (!isMounted) {
          return;
        }

        if (exists) {
          queryClient.invalidateQueries();
          setLoggedIn(true);
          setFetchAccounts(true);
        }
      })
      .finally(() => {
        if (isMounted) {
          setTimeout(() => {
            setIsSessionCheckLoading(false);
          }, 500); // Adding a small delay to improve UX by showing the spinner for a short time even if the session check is very fast.
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <div className="flex flex-col items-center justify-center w-full gap-4">
      <AlertDialog open={openWallet} onOpenChange={setOpenWallet}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Innskráning</AlertDialogTitle>
            <AlertDialogDescription>
              {/** TODO: Fix this text */}
              Með því að skrá þig inn með rafræn skilríkjum eða auðkennisappinu
              getur þú breytt eða bætt við reikningum.{" "}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <RadioGroup
            defaultValue="audkenniSim"
            value={authTab}
            onValueChange={(value) => setAuthTab(value)}
            className={"flex flex-col md:flex-row"}
          >
            <FieldLabel htmlFor="audkenniSim">
              <Field orientation="horizontal">
                <FieldContent>
                  <FieldTitle>Rafræn skilríki</FieldTitle>
                </FieldContent>
                <RadioGroupItem value="audkenniSim" id="audkenniSim" />
              </Field>
            </FieldLabel>
            <FieldLabel htmlFor="audkenniApp">
              <Field orientation="horizontal">
                <FieldContent>
                  <FieldTitle>Auðkennisapp</FieldTitle>
                </FieldContent>
                <RadioGroupItem value="audkenniApp" id="audkenniApp" />
              </Field>
            </FieldLabel>
          </RadioGroup>
          <div>
            {authTab == "audkenniSim" ? (
              <div className="flex flex-col gap-4 w-full">
                <Input
                  id="phoneNumber"
                  placeholder="Símanúmer"
                  maxLength={7}
                  onChange={(e) =>
                    setAuthPhoneNumber(e.target.value.replace(/\D/g, ""))
                  }
                  disabled={authenticating}
                  value={authPhoneNumber}
                  className="h-12"
                  inputMode="tel"
                  pattern="[0-9]*"
                  autoComplete="tel"
                />
              </div>
            ) : (
              <div>
                <Input
                  id="externalId"
                  placeholder="Kennitala"
                  maxLength={10}
                  disabled={authenticating}
                  onChange={(e) =>
                    setAuthExternalId(e.target.value.replace(/\D/g, ""))
                  }
                  value={authExternalId}
                  className="h-12"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="off"
                />
              </div>
            )}
          </div>
          <AlertDialogFooter className="flex-col-reverse sm:flex-col-reverse w-full">
            <AlertDialogCancel
              onClick={cancelAuth}
              className={`${FULL_WIDTH_TALL_BUTTON} shrink-0 ${OUTLINE_PRESSABLE_BUTTON}`}
            >
              Hætta við
            </AlertDialogCancel>
            <Button
              onClick={authenticate}
              disabled={authenticating}
              className={
                authenticating
                  ? `${FULL_WIDTH_TALL_BUTTON} shrink-0 ${GREEN_ACTION_BUTTON_DISABLED}`
                  : `${FULL_WIDTH_TALL_BUTTON} shrink-0 ${GREEN_ACTION_BUTTON_ENABLED}`
              }
            >
              Auðkenna
              {authenticating && <Spinner />}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={openAddAccount} onOpenChange={setOpenAddAccount}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Nýr bankareikningur</AlertDialogTitle>
            <AlertDialogDescription>
              Bættu við reikning til að greiða með.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AddAccount
            bank={accountBank}
            setBank={setAccountBank}
            hb={accountHb}
            setHb={setAccountHb}
            reikn={accountReikn}
            setReikn={setAccountReikn}
            corporateId={corporateId}
            setCorporateId={setCorporateId}
            addAccount={addAccount}
            isCorporate={selectedTab === "corporateUser"}
            inModal
          />
          <AlertDialogFooter className="flex-col-reverse sm:flex-col-reverse w-full">
            <AlertDialogCancel
              onClick={() => setOpenAddAccount(false)}
              className={`${FULL_WIDTH_TALL_BUTTON} shrink-0 ${OUTLINE_PRESSABLE_BUTTON}`}
            >
              Hætta við
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <Card className="w-full gap-0">
        <CardHeader>
          <CardTitle className="text-3xl flex justify-between flex-col gap-2">
            <span className="text-center w-full">
              {payment?.amount
                ? new Intl.NumberFormat("de-DE").format(payment.amount)
                : ""}{" "}
              kr.
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="w-full text-center">
          {payment && (
            <PaymentHeader
              paymentType={String(payment?.type)}
              payment={payment}
            />
          )}
        </CardContent>
      </Card>
      <Tabs
        defaultValue="user"
        className="w-full"
        value={selectedTab}
        onValueChange={(value) => setSelectedTab(value)}
      >
        <TabsList className="w-full">
          <TabsTrigger value="user">Einstaklingur</TabsTrigger>
          <TabsTrigger value="corporateUser">Fyrirtæki</TabsTrigger>
        </TabsList>
        <TabsContent value="user" className="pt-2">
          <>
            {isSessionCheckLoading ? (
              <div className="flex items-center justify-center py-8">
                <Spinner className="size-6" />
              </div>
            ) : loggedIn ? (
              <div className="flex flex-col gap-2">
                {accounts?.map((bankAccount, index) => (
                  <BankAccount
                    bankAccount={bankAccount}
                    selectAccount={(account) => onInitWalletPayment(account)}
                    isLoading={isLoading}
                    className={
                      index === 0
                        ? "shadow-[0_0_22px_rgba(140,238,123,0.55)] ring-1 ring-[#8CEE7B]/50"
                        : undefined
                    }
                  />
                ))}
                <Button
                  type="button"
                  variant="outline"
                  className={`${FULL_WIDTH_TALL_BUTTON} ${OUTLINE_PRESSABLE_BUTTON}`}
                  onClick={() => setOpenAddAccount(true)}
                >
                  <div className="flex items-center gap-1">
                    <span>Bæta við reikning</span>
                    <AddWalletIcon className="size-4 shrink-0" />
                  </div>
                </Button>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    variant={"outline"}
                    className={`${FULL_WIDTH_TALL_BUTTON} ${OUTLINE_PRESSABLE_BUTTON}`}
                    onClick={() => cancelPaymentMutation.mutate()}
                    disabled={cancelPaymentMutation.isPending}
                  >
                    <div className="flex items-center gap-1">
                      <span>Hætta við</span>
                      {cancelPaymentMutation.isPending ? (
                        <Spinner className="size-4" />
                      ) : (
                        <CircleX />
                      )}
                    </div>
                  </Button>
                  <Button
                    type="button"
                    variant={"outline"}
                    className={`${FULL_WIDTH_TALL_BUTTON} ${OUTLINE_PRESSABLE_BUTTON}`}
                    onClick={logout}
                  >
                    <div className="flex items-center gap-1">
                      <span>Útskrá</span>
                      <LogOut />
                    </div>
                  </Button>
                </div>
              </div>
            ) : (
              <InitPaymentUser
                status={String(payment?.status)}
                paymentId={String(payment?.id)}
                expiresAt={Number(payment?.expiresAt)}
                isLoading={isLoading}
                setRefetchInterval={() => setRefetchInterval(750)}
                setIsLoading={setIsLoading}
                openWallet={() => setOpenWallet(true)}
                setAuthPhoneNumber={setAuthPhoneNumber}
              />
            )}
          </>
        </TabsContent>
        {/** TODO: Need to think about how we are going to use corporate users */}
        <TabsContent value="corporateUser" className="pt-2">
          <>
            {isSessionCheckLoading ? (
              <div className="flex items-center justify-center py-8">
                <Spinner className="size-6" />
              </div>
            ) : loggedIn ? (
              <div className="flex flex-col gap-2">
                <CorporateUsers
                  corporateUsers={corporateUsers}
                  selectedCorporateUserId={selectedCorporateUserId}
                  setSelectedCorporateUserId={(corporateUserId) =>
                    setSelectedCorporateUserId(corporateUserId)
                  }
                  accounts={corporateAccounts}
                  selectAccount={(account) => onInitWalletPayment(account)}
                  isLoading={isLoading}
                />
                <Button
                  type="button"
                  variant="outline"
                  className={`${FULL_WIDTH_TALL_BUTTON} ${OUTLINE_PRESSABLE_BUTTON}`}
                  onClick={() => setOpenAddAccount(true)}
                >
                  <div className="flex items-center gap-1">
                    <span>Bæta við reikning</span>
                    <AddWalletIcon className="size-4 shrink-0" />
                  </div>
                </Button>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    variant={"outline"}
                    className={`${FULL_WIDTH_TALL_BUTTON} ${OUTLINE_PRESSABLE_BUTTON}`}
                    onClick={() => cancelPaymentMutation.mutate()}
                    disabled={cancelPaymentMutation.isPending}
                  >
                    <div className="flex items-center gap-1">
                      <span>Hætta við</span>
                      {cancelPaymentMutation.isPending ? (
                        <Spinner className="size-4" />
                      ) : (
                        <CircleX />
                      )}
                    </div>
                  </Button>
                  <Button
                    type="button"
                    variant={"outline"}
                    className={`${FULL_WIDTH_TALL_BUTTON} ${OUTLINE_PRESSABLE_BUTTON}`}
                    onClick={logout}
                  >
                    <div className="flex items-center gap-1">
                      <span>Útskrá</span>
                      <LogOut />
                    </div>
                  </Button>
                </div>
              </div>
            ) : (
              <InitPaymentCorporateUser
                status={String(payment?.status)}
                paymentId={String(payment?.id)}
                expiresAt={Number(payment?.expiresAt)}
                isLoading={isLoading}
                setRefetchInterval={() => setRefetchInterval(750)}
                setIsLoading={setIsLoading}
                openWallet={() => setOpenWallet(true)}
                setAuthPhoneNumber={setAuthPhoneNumber}
                setAuthExternalId={setAuthExternalId}
              />
            )}
          </>
        </TabsContent>
      </Tabs>
    </div>
  );
}
